/**
 * First-party cookie banner + Consent Mode v2 (`consent-gtag.ts`). Not an IAB TCF-certified CMP;
 * EEA/UK/CH traffic may get limited or non-personalized ads when ads consent is off or per Google policy.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CookieConsent from './CookieConsent';
import { getStoredConsent, setStoredConsent } from '../utils/consent-storage';
import { pushConsentToGtag } from '../utils/consent-gtag';
import { trackConsentGrantedPageView } from '../utils/analytics';
import { loadAdSense } from '../utils/load-adsense';

const ConsentUiContext = createContext({
  openCookieSettings: () => {},
});

export function useConsentUi() {
  return useContext(ConsentUiContext);
}

export function ConsentProvider({ children, translate }) {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const existing = getStoredConsent();
    if (existing) {
      pushConsentToGtag(existing);
    } else {
      setBannerOpen(true);
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    let idleId;
    let timeoutId;
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => loadAdSense(), { timeout: 2500 });
      } else {
        timeoutId = window.setTimeout(loadAdSense, 1500);
      }
    };
    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
    return () => {
      window.removeEventListener('load', schedule);
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, []);

  const openCookieSettings = useCallback(() => {
    setBannerOpen(true);
  }, []);

  const saveConsent = useCallback((consent) => {
    const wasGranted = getStoredConsent()?.analytics === true;
    setStoredConsent(consent);
    pushConsentToGtag(consent);
    if (consent.analytics && !wasGranted) {
      trackConsentGrantedPageView();
    }
    try {
      window.dispatchEvent(new CustomEvent('tvroulette-consent-updated', { detail: consent }));
    } catch {
      /* ignore */
    }
    setBannerOpen(false);
  }, []);

  const value = useMemo(() => ({ openCookieSettings }), [openCookieSettings]);

  const cookieLabels = translate?.cookie || {};

  return (
    <ConsentUiContext.Provider value={value}>
      {children}
      {hydrated && bannerOpen ? (
        <CookieConsent
          labels={cookieLabels}
          privacyPath={translate.language === 'en' ? '/privacy/' : `/${translate.language}/privacy/`}
          initial={getStoredConsent()}
          onSave={saveConsent}
          onClose={() => setBannerOpen(false)}
        />
      ) : null}
    </ConsentUiContext.Provider>
  );
}
